import { ChildModel, ModelBase } from 'store/model-base';
import { NowLineDevice } from './device';
import { LineStatus, TradeInProgram } from 'my-account/constants';

export class NowLineDetail extends ModelBase {
    public static storeName: string = 'nowLineDetail';
    public static idAttribute: string = 'id.key';
    public id: Id;
    public displayName: string;
    public lineNumber: string;
    public status: LineStatus;
    public currentPlan: CurrentPlan;
    public currentDataUsage: number; // in MB
    public dpfoEnforced: boolean;
    public devices: NowLineDevice[];
    public iconLetter: string;
    public displayStatus: string;
    public pairedLines: PairedLine[];
    public tradeInDetails: TradeInDetails;

    public isActive: boolean;
    public isPending: boolean;
    public isSuspended: boolean;
    public isDeactivated: boolean;
    public hasNDELorUpgrade: boolean;
    public isUnlimited: boolean;
    public isActiveOrSuspended: boolean;
    public isPendingOrDeactivated: boolean;
    public isTradeInUpgrade: boolean;
    public isLineEligibilityStatus: boolean;
    public hasTradeInUpgrade: boolean;

    public static create<T extends ModelBase>(data: ApiResponse = {}): T {
        data.id = data.id || { key: '', value: '' };
        data.displayName = data.displayName || '';
        data.lineNumber = data.lineNumber || '';
        data.status = data.status || '';
        data.currentDataUsage = data.currentDataUsage || 0;
        data.devices = data.devices || [];
        data.devices = data.devices.map((device: NowLineDevice) => ({ ...device, lineDisplayName: data.displayName, lineId: data.id }));
        data.iconLetter = data.displayName.substr(0, 1);

        data.isActive = data.status.toUpperCase() === LineStatus.ACTIVE;
        data.isPending = data.status.toUpperCase() === LineStatus.PENDING;
        data.isSuspended = data.status.toUpperCase() === LineStatus.SUSPENDED;
        data.isDeactivated = data.status.toUpperCase() === LineStatus.DEACTIVATED;
        data.isUnlimited = data.currentPlan.dataUnlimited;
        data.isActiveOrSuspended = data.isActive || data.isSuspended;
        data.isPendingOrDeactivated = data.isPending || data.isDeactivated;
        data.hasTradeInUpgrade = Boolean(data.tradeInDetails);
        data.isTradeInUpgrade = data.hasTradeInUpgrade && data.tradeInDetails.program === TradeInProgram.UPGRADE;
        data.isLineEligibilityStatus = (data.isActive || data.isSuspended) && !data.isTradeInUpgrade;

        if (data.pairedLines) {
            data.pairedLines = data.pairedLines.map((pairedLine: PairedLine) => {
                pairedLine.lineId = pairedLine.lineId || pairedLine.id;

                return pairedLine;
            });
        } else {
            data.pairedLines = [];
        }

        const toReturn: NowLineDetail = super.create<NowLineDetail>(data);
        toReturn.hasNDELorUpgrade = Boolean(data.devices.find((device: NowLineDevice) => device.isNDEL || device.isUpgrade));

        return super.create<T>(toReturn);
    }

    protected static get hasMany(): ChildModel[] {
        return [{
            attrName: 'devices',
            model: NowLineDevice
        }];
    }

    public getDeviceByKey(key: string): NowLineDevice {
        return this.devices.find((device: NowLineDevice) => device.id.key === key);
    }

    public getDeviceByValue(value: string): NowLineDevice {
        return this.devices.find((device: NowLineDevice) => device.id.value === value);
    }

    public getDeviceByOfferId(tradeinOfferId: string): NowLineDevice {
        return this.devices.find((device: NowLineDevice) => device.subsidy && device.subsidy.tradeinOfferId === tradeinOfferId);
    }

    public get activeDevice(): NowLineDevice {
        return this.devices.find((device: NowLineDevice) => device.isActive || device.isDppOutstanding);
    }

    public get pendingDevice(): NowLineDevice {
        return this.devices.find((device: NowLineDevice) => device.isPending || device.isNDEL || device.isUpgrade || device.isExchange || device.isByodExchange || device.isNewSim);
    }

    public get isPhone(): boolean {
        return this.activeDevice ? this.activeDevice.isPhone : this.pendingDevice.isPhone;
    }

    public get isTablet(): boolean {
        return this.activeDevice ? this.activeDevice.isTablet : this.pendingDevice.isTablet;
    }

    public get isWatch(): boolean {
        return this.activeDevice ? this.activeDevice.isWatch : this.pendingDevice.isWatch;
    }

    public get isRFA(): boolean {
        return this.status === LineStatus.READY_FOR_ACTIVATION;
    }
}
